import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { normalizeExternalUrl } from 'utils/common';
import {
  buildOwnershipGraph,
  edgeBands,
  IOwnershipEdge,
  IOwnershipGraph,
  isFormerEdge,
  OwnershipBand,
  sameNameNote,
  subjectConnectionCount,
} from 'utils/beneficialOwnershipGraph';
import { TreeWindowDialog } from 'components/Common/treeWindowDialog';

interface IOwnershipTreeProps {
  ticket: any;
  /**
   * Set when the tree opens as its own window over a report: a head with a
   * close button, and a fixed inspector beside the tree in place of the
   * click popover.
   */
  dialog?: TreeWindowDialog;
}

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// The card the inspector opens on: the first sanctioned connection within two
// steps of the subject, with its parent opened so the card is on screen.
const firstFlagged = (
  graph: IOwnershipGraph,
): { key: string; open?: string } | null => {
  const subject = graph.subject;
  if (!subject || (graph.branches || []).length) {
    return null;
  }
  const top = (graph.edges[subject] || []).filter((edge) => {
    const node = graph.nodes[edge.to];
    return !isFormerEdge(edge, node ? node.findings : '');
  });
  const direct = top.find((edge) => {
    const node = graph.nodes[edge.to];
    return Boolean(node && node.flagged);
  });
  if (direct) {
    return { key: `${subject}>${direct.to}` };
  }
  for (let i = 0; i < top.length; i += 1) {
    const parent = top[i].to;
    const child = (graph.edges[parent] || []).find((edge) => {
      const node = graph.nodes[edge.to];
      return edge.to !== subject && Boolean(node && node.flagged);
    });
    if (child) {
      return {
        key: `${subject}>${parent}>${child.to}`,
        open: `${subject}>${parent}`,
      };
    }
  }
  return null;
};

const BANDS: Array<{ key: OwnershipBand; label: string }> = [
  { key: 'owners', label: 'Owners & controllers' },
  { key: 'investors', label: 'Investors' },
  { key: 'holdings', label: 'Holdings & subsidiaries' },
  { key: 'people', label: 'Officers & directors' },
  { key: 'other', label: 'Other' },
];

const BAND_CAP = 10;
const MAX_DEPTH = 4;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2;
// Each band column steps this far below the previous one — the staggered look.
const STAGGER = 30;

interface IPopover {
  key: string;
  top: number;
  left: number;
  maxHeight: number;
  maxWidth: number;
  anchor: {
    top: number;
    bottom: number;
    left: number;
  };
}

const POPOVER_GAP = 8;
const POPOVER_MARGIN = 12;

const OwnershipTree: React.FC<IOwnershipTreeProps> = ({ ticket, dialog }) => {
  const graph: IOwnershipGraph = useMemo(
    () => buildOwnershipGraph(ticket),
    [ticket],
  );
  const opening = useMemo(
    () => (dialog ? firstFlagged(graph) : null),
    // Only the subject decides where the window opens; see the reset below.
    [graph.subject, Boolean(dialog)],
  );

  const [focus, setFocus] = useState(graph.subject);
  const [trail, setTrail] = useState<string[]>(
    graph.subject ? [graph.subject] : [],
  );
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>(
    opening && opening.open ? { [opening.open]: true } : {},
  );
  // The dialog's inspector: the card key it shows (path from the focus).
  const [selected, setSelected] = useState<string | null>(
    opening ? opening.key : null,
  );
  const [collapsedHubs, setCollapsedHubs] = useState<{
    [key: string]: boolean;
  }>({});
  const [expandedBands, setExpandedBands] = useState<{
    [key: string]: boolean;
  }>({});
  const [showFormer, setShowFormer] = useState<{ [key: string]: boolean }>({});
  const [query, setQuery] = useState('');
  const [popover, setPopover] = useState<IPopover | null>(null);

  const [zoom, setZoom] = useState(1);
  const [wirePath, setWirePath] = useState('');
  const fitModeRef = useRef(true);

  const stageRef = useRef<HTMLDivElement>(null);
  const fitboxRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Ticket.tsx renders <OwnershipTree ticket={cur_case.data} /> without a key,
  // and (per its own comment) reuses the Ticket instance across ticket
  // navigation. Moving between two BO tickets while this tab is open therefore
  // hands us a new graph while `focus`/`trail` still point at the PREVIOUS
  // subject -- graph.nodes[focus] is then undefined and the tree renders its
  // empty state for an investigation that has data.
  //
  // Keyed on the subject rather than the graph object: a background refetch of
  // the SAME ticket rebuilds the graph too, and blowing away the user's
  // expansion/zoom on a refresh would be its own bug.
  useEffect(() => {
    setFocus(graph.subject);
    setTrail(graph.subject ? [graph.subject] : []);
    setExpanded(opening && opening.open ? { [opening.open]: true } : {});
    setSelected(opening ? opening.key : null);
    setCollapsedHubs({});
    setExpandedBands({});
    setShowFormer({});
    setQuery('');
    setPopover(null);
    setZoom(1);
    setWirePath('');
    fitModeRef.current = true;
    if (stageRef.current) {
      stageRef.current.scrollTop = 0;
      stageRef.current.scrollLeft = 0;
    }
  }, [graph.subject]);

  const focusNode = graph.nodes[focus];

  const matchesQuery = useCallback(
    (edge: IOwnershipEdge) => {
      const needle = query.trim().toLowerCase();
      if (!needle) {
        return true;
      }
      const node = graph.nodes[edge.to];
      return `${node ? node.name : edge.to} ${edge.relation} ${
        node ? node.country : ''
      } ${node ? node.type : ''}`
        .toLowerCase()
        .includes(needle);
    },
    [graph, query],
  );

  const isFlagged = useCallback(
    (edge: IOwnershipEdge) => {
      const node = graph.nodes[edge.to];
      return Boolean(node && node.flagged);
    },
    [graph],
  );

  const isFormer = useCallback(
    (edge: IOwnershipEdge) => {
      const node = graph.nodes[edge.to];
      return isFormerEdge(edge, node ? node.findings : '');
    },
    [graph],
  );

  // ---- fit-to-width zoom ----
  const relayout = useCallback((nextZoom: number) => {
    const chart = chartRef.current;
    const fitbox = fitboxRef.current;
    if (!chart || !fitbox) {
      return;
    }
    fitbox.style.width = `${chart.offsetWidth * nextZoom}px`;
    fitbox.style.height = `${chart.offsetHeight * nextZoom}px`;
  }, []);

  const computeFit = useCallback(() => {
    const stage = stageRef.current;
    const chart = chartRef.current;
    if (!stage || !chart || !chart.offsetWidth) {
      return 1;
    }
    return Math.max(
      ZOOM_MIN,
      Math.min(1, (stage.clientWidth - 30) / chart.offsetWidth),
    );
  }, []);

  // Measured orthogonal connectors from the subject down to each band-column
  // hub, recomputed whenever the layout or zoom changes.
  const drawWires = useCallback((currentZoom: number) => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    const chartRect = chart.getBoundingClientRect();
    const pos = (el: Element) => {
      const r = el.getBoundingClientRect();
      return {
        l: (r.left - chartRect.left) / currentZoom,
        t: (r.top - chartRect.top) / currentZoom,
        w: r.width / currentZoom,
        h: r.height / currentZoom,
      };
    };
    // Every wired element names its parent card: band hubs point at the card
    // above them (the subject, or a jurisdiction branch), and branch cards
    // point at the subject.
    let d = '';
    Array.prototype.forEach.call(
      chart.querySelectorAll('[data-wire-parent]'),
      (child: Element) => {
        const parent = chart.querySelector(
          `[data-wire-id="${child.getAttribute('data-wire-parent')}"]`,
        );
        if (!parent) {
          return;
        }
        const sp = pos(parent);
        const sx = sp.l + sp.w / 2;
        const sy = sp.t + sp.h;
        const hp = pos(child);
        const hx = hp.l + hp.w / 2;
        d += `M${sx} ${sy} V${sy + 16} H${hx} V${hp.t} `;
      },
    );
    setWirePath((previous) => (previous === d ? previous : d));
  }, []);

  // After any render that changes the chart: fit (if in fit mode) then wires.
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      const next = fitModeRef.current ? computeFit() : zoom;
      relayout(next);
      if (fitModeRef.current && next !== zoom) {
        setZoom(next);
      }
      drawWires(next);
    });
    return () => cancelAnimationFrame(raf);
  }, [
    focus,
    expanded,
    collapsedHubs,
    expandedBands,
    showFormer,
    query,
    zoom,
    computeFit,
    relayout,
    drawWires,
  ]);

  useEffect(() => {
    let timer: any = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (fitModeRef.current) {
          const next = computeFit();
          relayout(next);
          setZoom(next);
        }
      }, 120);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timer);
    };
  }, [computeFit, relayout]);

  const applyFit = () => {
    fitModeRef.current = true;
    const next = computeFit();
    relayout(next);
    setZoom(next);
  };

  const nudgeZoom = (direction: number) => {
    fitModeRef.current = false;
    setZoom((previous) => {
      const next = Math.max(
        ZOOM_MIN,
        Math.min(
          ZOOM_MAX,
          Math.round(previous * (direction > 0 ? 1.2 : 1 / 1.2) * 1000) / 1000,
        ),
      );
      relayout(next);
      return next;
    });
  };

  const goTo = (key: string) => {
    const at = trail.indexOf(key);
    setTrail(at !== -1 ? trail.slice(0, at + 1) : trail.concat([key]));
    setFocus(key);
    setExpanded({});
    setSelected(dialog ? key : null);
    setCollapsedHubs({});
    setExpandedBands({});
    setShowFormer({});
    setQuery('');
    setPopover(null);
    fitModeRef.current = true;
    if (stageRef.current) {
      stageRef.current.scrollTop = 0;
      stageRef.current.scrollLeft = 0;
    }
  };

  const resetView = () => {
    setFocus(graph.subject);
    setTrail(graph.subject ? [graph.subject] : []);
    setExpanded(opening && opening.open ? { [opening.open]: true } : {});
    setSelected(opening ? opening.key : null);
    setCollapsedHubs({});
    setExpandedBands({});
    setShowFormer({});
    setQuery('');
    setPopover(null);
    fitModeRef.current = true;
  };

  // A card click: the dialog shows the card in its inspector (a second click
  // clears it); inline, the popover opens under the card.
  const pick = (key: string, event: React.MouseEvent) => {
    if (dialog) {
      setSelected((current) => (current === key ? null : key));
      return;
    }
    openPopover(key, event);
  };

  const openPopover = (key: string, event: React.MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 380;
    const left = Math.min(
      Math.max(POPOVER_MARGIN, rect.left),
      window.innerWidth - width - POPOVER_MARGIN,
    );
    setPopover({
      key: key,
      top: rect.bottom + POPOVER_GAP,
      left: Math.max(POPOVER_MARGIN, left),
      maxHeight: Math.max(160, window.innerHeight - POPOVER_MARGIN * 2),
      maxWidth: Math.max(1, window.innerWidth - POPOVER_MARGIN * 2),
      anchor: {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
      },
    });
  };

  // The findings text varies substantially, so a fixed assumed popover height
  // can push long records through the tree shell and over the app footer.
  // Measure the rendered card, prefer the space below its node, flip above when
  // needed, and constrain unusually long content to the visible tree stage.
  useLayoutEffect(() => {
    const detail = popoverRef.current;
    const stage = stageRef.current;
    if (!popover || !detail || !stage) {
      return;
    }

    const detailRect = detail.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const minTop = Math.max(POPOVER_MARGIN, stageRect.top + POPOVER_MARGIN);
    const maxBottom = Math.min(
      window.innerHeight - POPOVER_MARGIN,
      stageRect.bottom - POPOVER_MARGIN,
    );
    const minLeft = Math.max(POPOVER_MARGIN, stageRect.left + POPOVER_MARGIN);
    const maxRight = Math.min(
      window.innerWidth - POPOVER_MARGIN,
      stageRect.right - POPOVER_MARGIN,
    );
    const belowTop = popover.anchor.bottom + POPOVER_GAP;
    const aboveBottom = popover.anchor.top - POPOVER_GAP;
    const belowSpace = Math.max(0, maxBottom - belowTop);
    const aboveSpace = Math.max(0, aboveBottom - minTop);
    const naturalHeight = Math.max(detailRect.height, detail.scrollHeight + 2);

    let top = belowTop;
    let maxHeight = Math.max(1, maxBottom - minTop);
    if (naturalHeight <= belowSpace) {
      maxHeight = belowSpace;
    } else if (naturalHeight <= aboveSpace) {
      maxHeight = aboveSpace;
      top = aboveBottom - naturalHeight;
    } else if (aboveSpace > belowSpace) {
      maxHeight = aboveSpace;
      top = minTop;
    } else {
      maxHeight = belowSpace;
    }

    const renderedHeight = Math.min(naturalHeight, maxHeight);
    const maxWidth = Math.max(1, maxRight - minLeft);
    const renderedWidth = Math.min(detailRect.width, maxWidth);
    top = Math.min(
      Math.max(minTop, top),
      Math.max(minTop, maxBottom - renderedHeight),
    );
    const left = Math.min(
      Math.max(minLeft, popover.anchor.left),
      Math.max(minLeft, maxRight - renderedWidth),
    );
    const roundedTop = Math.round(top);
    const roundedLeft = Math.round(left);
    const roundedMaxHeight = Math.max(1, Math.floor(maxHeight));
    const roundedMaxWidth = Math.max(1, Math.floor(maxWidth));

    if (
      roundedTop !== Math.round(popover.top) ||
      roundedLeft !== Math.round(popover.left) ||
      roundedMaxHeight !== Math.round(popover.maxHeight) ||
      roundedMaxWidth !== Math.round(popover.maxWidth)
    ) {
      setPopover((current) =>
        current
          ? {
              ...current,
              top: roundedTop,
              left: roundedLeft,
              maxHeight: roundedMaxHeight,
              maxWidth: roundedMaxWidth,
            }
          : current,
      );
    }
  }, [popover]);

  useEffect(() => {
    if (!popover) {
      return undefined;
    }
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bo-tree-pop') && !target.closest('.bo-tree-node')) {
        setPopover(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [popover]);

  useEffect(() => {
    if (!popover) {
      return undefined;
    }
    const close = () => setPopover(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [popover]);

  if (graph.isEmpty || !focusNode) {
    return (
      <div className='bo-tree-shell'>
        <div className='bo-tree-empty'>
          No ownership graph was saved with this investigation.
        </div>
      </div>
    );
  }

  const isPicked = (key: string) =>
    dialog ? selected === key : popover ? popover.key === key : false;

  const avatar = (kind: 'company' | 'person') => (
    <span className={`bo-tree-avatar${kind === 'person' ? ' person' : ''}`}>
      {kind === 'person' ? (
        <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />
      ) : (
        <ApartmentOutlinedIcon sx={{ fontSize: 15 }} />
      )}
    </span>
  );

  // The subject's name matched companies in several countries and no
  // jurisdiction was picked: the subject card is only the search term, and each
  // country's company gets its own branch of band columns below it.
  const branches = focus === graph.subject ? graph.branches || [] : [];

  const renderSubjectCard = () => {
    const childCount = branches.length
      ? subjectConnectionCount(graph)
      : (graph.edges[focus] || []).length;
    const Card: any = dialog ? 'button' : 'div';
    const subjectSelected = Boolean(dialog) && selected === focus;
    return (
      <Card
        {...(dialog
          ? {
              type: 'button',
              'aria-pressed': subjectSelected,
              onClick: (event: React.MouseEvent) => pick(focus, event),
            }
          : {})}
        className={`bo-tree-node subject${subjectSelected ? ' sel' : ''}`}
        data-wire-id='root'
      >
        <div className='bo-tree-node-top'>
          {avatar(focusNode.kind)}
          <div className='bo-tree-node-id'>
            <div className='bo-tree-eyebrow'>
              {focus === graph.subject
                ? 'Subject of this investigation'
                : 'In focus'}
            </div>
            <div className='bo-tree-node-name'>{focusNode.name}</div>
            <div className='bo-tree-node-role'>
              {branches.length
                ? `Found in ${branches.length} jurisdictions`
                : [focusNode.type, focusNode.country]
                    .filter(Boolean)
                    .join(' · ')}
            </div>
            {focus === graph.subject && sameNameNote(graph) && (
              <div className='bo-tree-subject-note'>{sameNameNote(graph)}</div>
            )}
          </div>
        </div>
        <div className='bo-tree-node-foot'>
          <span className='b'>
            <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />
            {childCount} connection{childCount === 1 ? '' : 's'}
          </span>
        </div>
      </Card>
    );
  };

  const renderCard = (
    edge: IOwnershipEdge,
    key: string,
    depth: number,
    former: boolean,
    isCycle: boolean,
  ) => {
    const node = graph.nodes[edge.to];
    const name = node ? node.name : edge.to;
    const childCount = (graph.edges[edge.to] || []).length;
    const canExpand = childCount > 0 && depth < MAX_DEPTH - 1 && !isCycle;
    const isOpen = Boolean(expanded[key]);
    const isSelected = isPicked(key);

    return (
      <button
        type='button'
        aria-pressed={dialog ? isSelected : undefined}
        className={`bo-tree-node${node && node.flagged ? ' flag' : ''}${
          former ? ' former' : ''
        }${isSelected ? ' sel' : ''}${isOpen ? ' open' : ''}`}
        onClick={(event) => pick(key, event)}
      >
        <div className='bo-tree-node-top'>
          {avatar(node && node.kind === 'person' ? 'person' : 'company')}
          <div className='bo-tree-node-id'>
            <div className='bo-tree-node-name' title={name}>
              {name}
            </div>
            <div
              className='bo-tree-node-role'
              title={`${edge.relation}${former ? ' · former' : ''}${
                node && node.country ? ` · ${node.country}` : ''
              }`}
            >
              {edge.relation}
              {former && ' · former'}
              {node && node.country ? ` · ${node.country}` : ''}
            </div>
          </div>
        </div>
        {(childCount > 0 || (node && node.flagged)) && (
          <div className='bo-tree-node-foot'>
            {isCycle && (
              <span className='bo-tree-node-cycle'>
                Cycle back to an entity already in this branch
              </span>
            )}
            {childCount > 0 && !isCycle && (
              <span className='b'>
                <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />
                {childCount}
              </span>
            )}
            {node && node.flagged && (
              <span className='bo-tree-node-pill'>Sanctioned</span>
            )}
          </div>
        )}
        {canExpand && (
          <span
            className='bo-tree-toggle'
            role='button'
            aria-label={
              isOpen ? 'Collapse affiliations' : 'Expand affiliations'
            }
            onClick={(event) => {
              event.stopPropagation();
              setExpanded({ ...expanded, [key]: !isOpen });
            }}
          >
            {isOpen ? '−' : '+'}
          </span>
        )}
      </button>
    );
  };

  const filteredChildren = (
    parentKey: string,
    reachedVia: OwnershipBand,
  ): IOwnershipEdge[] =>
    (graph.edges[parentKey] || []).filter((edge: IOwnershipEdge) => {
      // Any role counts: an owner who is also a director is still an officer
      // for this filter, exactly as their separate director edge used to be.
      const bands = edgeBands(edge);
      if (bands.indexOf('people') !== -1) {
        return true;
      }
      // Owners and investors are both "stakeholder" bands: keep the original
      // behaviour of surfacing them down an owner/investor-reached chain (before
      // investors had their own band they were classified as owners).
      const stakeholder = (band: OwnershipBand) =>
        band === 'owners' || band === 'investors';
      if (bands.some(stakeholder) && stakeholder(reachedVia)) {
        return true;
      }
      return isFlagged(edge);
    });

  // A node's affiliations as an indented, downward rail-tree. This is what an
  // expanded node shows, so expansion grows DOWN inside its column and can never
  // overlap a neighbouring column.
  const renderSubMembers = (
    parentKey: string,
    depth: number,
    path: string[],
    reachedVia: OwnershipBand,
  ): React.ReactNode => {
    const kids = filteredChildren(parentKey, reachedVia);
    if (!kids.length) {
      return null;
    }
    return (
      <ul className='bo-tree-mtree nested'>
        {kids.map((edge) => renderMember(edge, depth, path))}
      </ul>
    );
  };

  const renderMember = (
    edge: IOwnershipEdge,
    depth: number,
    path: string[],
  ): React.ReactNode => {
    const nodeKey = edge.to;
    const key = path.concat([nodeKey]).join('>');
    const former = isFormer(edge);
    // Same rule as the PDF tree: an entity already above this card in the
    // branch is shown once, labelled, and never expanded -- otherwise
    // subject -> owner -> subject repeats down to MAX_DEPTH.
    const isCycle = path.indexOf(nodeKey) !== -1;
    const isOpen = Boolean(expanded[key]) && depth < MAX_DEPTH && !isCycle;
    return (
      <li key={key}>
        {renderCard(edge, key, depth, former, isCycle)}
        {isOpen &&
          renderSubMembers(
            nodeKey,
            depth + 1,
            path.concat([nodeKey]),
            edge.band,
          )}
      </li>
    );
  };

  // One root's connections grouped into staggered band columns: the focus in
  // the single-company tree, or each jurisdiction branch. `path` seeds the
  // cycle check, so a branch's members treat the subject above it as an
  // ancestor too.
  const renderBands = (
    rootKey: string,
    wireId: string,
    path: string[],
  ): React.ReactNode[] => {
    const buckets: { [key: string]: IOwnershipEdge[] } = {
      owners: [],
      investors: [],
      holdings: [],
      people: [],
      other: [],
    };
    (graph.edges[rootKey] || []).forEach((edge: IOwnershipEdge) => {
      if (matchesQuery(edge)) {
        buckets[edge.band].push(edge);
      }
    });

    const columns: React.ReactNode[] = [];
    let staggerIndex = 0;
    BANDS.forEach((band) => {
      const raw = buckets[band.key];

      let list = raw;
      let formerCount = 0;
      let formerShown = false;
      if (band.key === 'people') {
        const former = raw.filter(isFormer);
        const current = raw.filter((edge) => !isFormer(edge));
        formerCount = former.length;
        formerShown = Boolean(showFormer[`${rootKey}|people`]);
        list = formerShown ? current.concat(former) : current;
      }
      const hasFormerToggle = band.key === 'people' && formerCount > 0;
      if (!list.length && !hasFormerToggle) {
        return;
      }

      const hubId = `${rootKey}|${band.key}`;
      const collapsed = Boolean(collapsedHubs[hubId]);
      const showAll = Boolean(expandedBands[hubId]);
      const shown = showAll ? list : list.slice(0, BAND_CAP);
      const hasMore = list.length > BAND_CAP;
      const marginTop = staggerIndex * STAGGER;
      staggerIndex += 1;

      columns.push(
        <div
          className='bo-tree-col'
          key={band.key}
          style={{ marginTop: marginTop }}
        >
          <button
            type='button'
            className={`bo-tree-hub${collapsed ? ' collapsed' : ''}`}
            data-wire-parent={wireId}
            onClick={() =>
              setCollapsedHubs({ ...collapsedHubs, [hubId]: !collapsed })
            }
          >
            <span className='caret'>{collapsed ? '▸' : '▾'}</span>
            {band.label}
            <span className='ct'>{raw.length}</span>
          </button>
          {!collapsed && (
            <ul className='bo-tree-mtree top'>
              {shown.map((edge) => renderMember(edge, 1, path))}
              {hasMore && (
                <li key='more'>
                  <button
                    type='button'
                    className='bo-tree-more'
                    onClick={() =>
                      setExpandedBands({ ...expandedBands, [hubId]: true })
                    }
                  >
                    +{list.length - BAND_CAP} more
                  </button>
                </li>
              )}
              {hasFormerToggle && (
                <li key='former'>
                  <button
                    type='button'
                    className='bo-tree-former-toggle'
                    aria-pressed={formerShown}
                    onClick={() =>
                      setShowFormer({
                        ...showFormer,
                        [`${rootKey}|people`]: !formerShown,
                      })
                    }
                  >
                    {formerShown
                      ? 'Hide former roles'
                      : `Show former roles (${formerCount})`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>,
      );
    });
    return columns;
  };

  // A jurisdiction branch: that country's company, wired up to the subject.
  // Clicking it opens the same detail popover as any card, so it can be
  // focused on to see only that company's tree.
  const renderBranchCard = (branch: string, wireId: string) => {
    const node = graph.nodes[branch];
    const key = `${graph.subject}>${branch}`;
    const childCount = (graph.edges[branch] || []).length;
    const isSelected = isPicked(key);
    return (
      <button
        type='button'
        aria-pressed={dialog ? isSelected : undefined}
        className={`bo-tree-node branch${node && node.flagged ? ' flag' : ''}${
          isSelected ? ' sel' : ''
        }`}
        data-wire-id={wireId}
        data-wire-parent='root'
        onClick={(event) => pick(key, event)}
      >
        <div className='bo-tree-node-top'>
          {avatar('company')}
          <div className='bo-tree-node-id'>
            <div className='bo-tree-eyebrow'>
              {(node && node.country) || 'Country not stated'}
            </div>
            <div
              className='bo-tree-node-name'
              title={node ? node.name : branch}
            >
              {node ? node.name : branch}
            </div>
            {node && node.type && (
              <div className='bo-tree-node-role' title={node.type}>
                {node.type}
              </div>
            )}
          </div>
        </div>
        <div className='bo-tree-node-foot'>
          <span className='b'>
            <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />
            {childCount} connection{childCount === 1 ? '' : 's'}
          </span>
          {node && node.flagged && (
            <span className='bo-tree-node-pill'>Sanctioned</span>
          )}
        </div>
      </button>
    );
  };

  const columns = branches.length ? [] : renderBands(focus, 'root', [focus]);

  const popKey = popover ? popover.key.split('>').pop() || '' : '';
  const popNode = popover ? graph.nodes[popKey] : null;
  const popConnections = popover ? graph.edges[popKey] || [] : [];
  const popUrl = popNode ? normalizeExternalUrl(popNode.sourceUrl) : '';

  const affiliationText = (connections: IOwnershipEdge[]) =>
    connections.length
      ? `${connections.length} affiliation${
          connections.length === 1 ? '' : 's'
        }: ${connections
          .slice(0, 6)
          .map((edge) => {
            const target = graph.nodes[edge.to];
            return target ? target.name : edge.to;
          })
          .join(', ')}${connections.length > 6 ? '…' : ''}`
      : 'No further affiliations recorded.';

  // ---- the dialog's inspector ----
  const selParts = dialog && selected ? selected.split('>') : [];
  const selKey = selParts.length ? selParts[selParts.length - 1] : '';
  const selNode = selKey ? graph.nodes[selKey] : null;
  const selConnections = selKey ? graph.edges[selKey] || [] : [];
  const selUrl = selNode ? normalizeExternalUrl(selNode.sourceUrl) : '';
  // How the card hangs off the focus, traced down the card's own path.
  const selPath = selParts.map((key, index) => {
    const node = graph.nodes[key];
    const name = node ? node.name : key;
    if (index === 0) {
      return {
        key: key,
        name: name,
        note:
          key === graph.subject ? 'Subject of this investigation' : 'In focus',
      };
    }
    const parentKey = selParts[index - 1];
    const parent = graph.nodes[parentKey];
    const edge = (graph.edges[parentKey] || []).find(
      (candidate) => candidate.to === key,
    );
    return {
      key: key,
      name: name,
      note: edge
        ? `${capitalize(edge.relation)} of ${parent ? parent.name : parentKey}`
        : node && node.country
        ? `Company of that name in ${node.country}`
        : 'Company of that name',
    };
  });
  const connectionTotal = branches.length
    ? subjectConnectionCount(graph)
    : (graph.edges[focus] || []).length;

  const stage = (
    <div
      className='bo-tree-stage'
      ref={stageRef}
      onScroll={() => {
        if (popover) {
          setPopover(null);
        }
      }}
    >
      <div className='bo-tree-fitbox' ref={fitboxRef}>
        <div className='bo-tree-scaler' style={{ transform: `scale(${zoom})` }}>
          <div className='bo-tree-chart' ref={chartRef}>
            <svg className='bo-tree-wires' aria-hidden='true'>
              {wirePath && <path d={wirePath} />}
            </svg>
            <div className='bo-tree-subject-row'>{renderSubjectCard()}</div>
            {branches.length ? (
              <div className='bo-tree-branches'>
                {branches.map((branch, index) => {
                  const wireId = `branch-${index}`;
                  const branchColumns = renderBands(branch, wireId, [
                    graph.subject,
                    branch,
                  ]);
                  return (
                    <div className='bo-tree-branch' key={branch}>
                      <div className='bo-tree-branch-head'>
                        {renderBranchCard(branch, wireId)}
                      </div>
                      {branchColumns.length ? (
                        <div className='bo-tree-cols'>{branchColumns}</div>
                      ) : (
                        <div className='bo-tree-empty'>
                          {query
                            ? `No connections match “${query}”.`
                            : 'No connections were saved for this company.'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : columns.length ? (
              <div className='bo-tree-cols'>{columns}</div>
            ) : (
              <div className='bo-tree-empty'>
                {query
                  ? `No connections match “${query}”.`
                  : 'No connections were saved for this entity.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (dialog) {
    const subjectNode = graph.nodes[graph.subject];
    return (
      <div className='bo-tree-shell is-dialog'>
        <div className='bo-tree-bar'>
          <span className='bo-tree-dialog-icon' aria-hidden='true'>
            <AccountTreeOutlinedIcon sx={{ fontSize: 19 }} />
          </span>
          <div className='bo-tree-dialog-title'>
            <h2 id={dialog.titleId}>Ownership tree</h2>
            {trail.length > 1 ? (
              <nav className='bo-tree-crumbs'>
                {trail.map((key: string, index: number) => {
                  const node = graph.nodes[key];
                  const isLast = index === trail.length - 1;
                  return (
                    <React.Fragment key={key}>
                      {index > 0 && <span className='sep'>›</span>}
                      <button
                        type='button'
                        disabled={isLast}
                        onClick={() => goTo(key)}
                      >
                        {node ? node.name : key}
                      </button>
                    </React.Fragment>
                  );
                })}
              </nav>
            ) : (
              <p className='tnum'>
                {[
                  subjectNode ? subjectNode.name : graph.subject,
                  `${connectionTotal} connection${
                    connectionTotal === 1 ? '' : 's'
                  }`,
                  dialog.meta,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
          <label className='bo-tree-search'>
            <SearchRoundedIcon sx={{ fontSize: 17 }} aria-hidden='true' />
            <input
              type='search'
              value={query}
              placeholder='Find an entity…'
              aria-label='Find an entity'
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className='bo-tree-zoom' role='group' aria-label='Zoom'>
            <button
              type='button'
              aria-label='Zoom out'
              onClick={() => nudgeZoom(-1)}
            >
              <RemoveRoundedIcon sx={{ fontSize: 16 }} />
            </button>
            <span className='zlabel' aria-live='polite'>
              {Math.round(zoom * 100)}%
            </span>
            <button
              type='button'
              aria-label='Zoom in'
              onClick={() => nudgeZoom(1)}
            >
              <AddRoundedIcon sx={{ fontSize: 16 }} />
            </button>
            <button type='button' className='fit' onClick={applyFit}>
              Fit
            </button>
          </div>
          <button type='button' className='bo-tree-btn' onClick={resetView}>
            Reset view
          </button>
          {dialog.action}
          <span className='bo-tree-bar-rule' aria-hidden='true' />
          <button
            type='button'
            className='bo-tree-close'
            aria-label='Close ownership tree'
            onClick={dialog.onClose}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='bo-tree-body'>
          {stage}
          <aside className='bo-tree-inspector' aria-label='Entity details'>
            {selNode ? (
              <>
                <div className='bo-tree-inspector-scroll'>
                  <div className='bo-tree-inspector-head'>
                    <span
                      className={`bo-tree-avatar lg${
                        selNode.kind === 'person' ? ' person' : ''
                      }`}
                    >
                      {selNode.kind === 'person' ? (
                        <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <ApartmentOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </span>
                    <div>
                      <h3>{selNode.name}</h3>
                      <div className='bo-tree-tags'>
                        {selNode.country && <span>{selNode.country}</span>}
                        {selNode.type && <span>{selNode.type}</span>}
                        {selNode.flagged && (
                          <span className='flag'>Sanctioned</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {selNode.findings && (
                    <>
                      <h4>Findings</h4>
                      <p>{selNode.findings}</p>
                    </>
                  )}

                  <h4>How it connects to the subject</h4>
                  <ol className='bo-tree-path'>
                    {selPath.map((step, index) => {
                      const last = index === selPath.length - 1;
                      return (
                        <li
                          key={step.key}
                          className={`${last ? 'last' : ''}${
                            last && selNode.flagged ? ' flag' : ''
                          }`}
                        >
                          <span className='dot' aria-hidden='true' />
                          <span className='name'>{step.name}</span>
                          <span className='note'>{step.note}</span>
                        </li>
                      );
                    })}
                  </ol>

                  <h4>Affiliations</h4>
                  <p>{affiliationText(selConnections)}</p>
                </div>
                <div className='bo-tree-inspector-acts'>
                  {selConnections.length > 0 && selKey !== focus && (
                    <button
                      type='button'
                      className='bo-tree-btn'
                      onClick={() => goTo(selKey)}
                    >
                      <CenterFocusStrongOutlinedIcon sx={{ fontSize: 16 }} />
                      Focus on this entity
                    </button>
                  )}
                  {selUrl && (
                    <a
                      className='bo-tree-source'
                      href={selUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      View source
                      <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
                    </a>
                  )}
                  {selNode.sourceDate && (
                    <span className='pop-date tnum'>
                      Source date {selNode.sourceDate}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className='bo-tree-inspector-empty'>
                <p className='lead'>Pick a person or company</p>
                <p>
                  Its findings, its path to the subject, and its source show
                  here.
                </p>
              </div>
            )}
          </aside>
        </div>

        <div className='bo-tree-legend'>
          <span>
            <i className='swatch co' aria-hidden='true' />
            Company
          </span>
          <span>
            <i className='swatch pe' aria-hidden='true' />
            Person
          </span>
          <span>
            <i className='swatch flag' aria-hidden='true' />
            Sanctioned
          </span>
          <span>
            <i className='swatch former' aria-hidden='true' />
            Former role
          </span>
          <span className='hint'>Scroll to move around · Esc closes</span>
        </div>
      </div>
    );
  }

  return (
    <div className='bo-tree-shell'>
      <div className='bo-tree-bar'>
        <nav className='bo-tree-crumbs'>
          {trail.map((key: string, index: number) => {
            const node = graph.nodes[key];
            const isLast = index === trail.length - 1;
            return (
              <React.Fragment key={key}>
                {index > 0 && <span className='sep'>›</span>}
                <button
                  type='button'
                  disabled={isLast}
                  onClick={() => goTo(key)}
                >
                  {node ? node.name : key}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <input
          className='bo-tree-filter'
          type='search'
          value={query}
          placeholder='Find an entity…'
          aria-label='Find an entity'
          onChange={(event) => {
            setQuery(event.target.value);
            setPopover(null);
          }}
        />
        <div className='bo-tree-zoom' role='group' aria-label='Zoom'>
          <button
            type='button'
            aria-label='Zoom out'
            onClick={() => nudgeZoom(-1)}
          >
            <RemoveRoundedIcon sx={{ fontSize: 16 }} />
          </button>
          <span className='zlabel' aria-live='polite'>
            {Math.round(zoom * 100)}%
          </span>
          <button
            type='button'
            aria-label='Zoom in'
            onClick={() => nudgeZoom(1)}
          >
            <AddRoundedIcon sx={{ fontSize: 16 }} />
          </button>
          <button type='button' className='fit' onClick={applyFit}>
            Fit
          </button>
        </div>
        <button type='button' className='bo-tree-btn' onClick={resetView}>
          Reset view
        </button>
      </div>

      {stage}

      {popover && popNode && (
        <div
          ref={popoverRef}
          className='bo-tree-pop'
          role='dialog'
          aria-label={`Details for ${popNode.name}`}
          style={{
            top: popover.top,
            left: popover.left,
            maxHeight: popover.maxHeight,
            maxWidth: popover.maxWidth,
          }}
        >
          <button
            type='button'
            className='close'
            aria-label='Close entity details'
            onClick={() => setPopover(null)}
          >
            ×
          </button>
          <h4>{popNode.name}</h4>
          <div className='tags'>
            {popNode.country && <span>{popNode.country}</span>}
            {popNode.type && <span>{popNode.type}</span>}
            {popNode.flagged && <span className='flag'>Sanctioned</span>}
          </div>
          {popNode.findings && <p>{popNode.findings}</p>}
          <div className='conns'>{affiliationText(popConnections)}</div>
          <div className='acts'>
            {popConnections.length > 0 && (
              <button
                type='button'
                className='primary'
                onClick={() => goTo(popKey)}
              >
                Focus on this entity
              </button>
            )}
            {popUrl && (
              <a href={popUrl} target='_blank' rel='noopener noreferrer'>
                View source
              </a>
            )}
            {popNode.sourceDate && (
              <span className='pop-date'>Source date {popNode.sourceDate}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnershipTree;
